import { Users, KeyRound } from "lucide-react";
import { cn } from "@/lib/utils";

export type EmployeesSectionView = "employees" | "logins";

interface EmployeesSectionToggleProps {
  value: EmployeesSectionView;
  onChange: (view: EmployeesSectionView) => void;
  className?: string;
}

/**
 * Compact segmented toggle that lets the Employees area host Login Management
 * as a sub-view instead of a separate top-level tab (keeps the tab bar short
 * for both the agency page and the client detail page).
 */
export function EmployeesSectionToggle({ value, onChange, className }: EmployeesSectionToggleProps) {
  const options: { value: EmployeesSectionView; label: string; icon: typeof Users }[] = [
    { value: "employees", label: "Employees", icon: Users },
    { value: "logins", label: "Login Management", icon: KeyRound },
  ];

  return (
    <div
      role="tablist"
      aria-label="Employees section"
      className={cn(
        "inline-flex items-center rounded-lg bg-muted p-1 text-muted-foreground",
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`employees-view-${opt.value}`}
            onClick={() => onChange(opt.value)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              active
                ? "bg-background text-foreground shadow-sm"
                : "hover:text-foreground",
            )}
          >
            <Icon className="h-4 w-4" />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
